<template>
  <div class="base-checkbox" @click.stop>
    <label>
      <div class="base-checkbox__wrapper">
        <input
          :checked="props.modelValue"
          :tabindex="props.tabindex"
          :disabled="props.readonly"
          type="checkbox"
          :class="{ 'base-checkbox__input--checked': props.modelValue }"
          @change.stop="handleChangeCheckbox"
          @keydown.enter.prevent="handleEnterKey"
        >
        <div class="base-checkbox__icon">
          <BaseIcon
            name="vuesax:tick-square-bold"
            :color="checkedcolorcss"
            fill-type="solid"
            size="var(--checkbox-icon-size)"
          />
        </div>
        <div class="base-checkbox__checkbox" />
      </div>

      <p v-if="props.text || $slots.default">
        <slot>
          {{ props.text }}
        </slot>
      </p>
      <slot v-else />
    </label>
  </div>
</template>

<script setup lang="ts">
import BaseIcon from '../icon/index.vue'

export interface Props {
  modelValue?: boolean
  tabindex?: number
  text?: string
  readonly?: boolean
  checkedColor?: string
}

const props = withDefaults(defineProps<Props>(), {
  checkedColor: 'var(--color-foreground-primary)',
})

const emit = defineEmits<{
  (e: 'update:modelValue', value: boolean): void
}>()

const checkedcolorcss = computed(() => props.checkedColor)

function handleChangeCheckbox(event: Event) {
  if (props.readonly) {
    return
  }

  event.preventDefault()
  const target = event.target as HTMLInputElement

  emit('update:modelValue', target.checked)
}

function handleEnterKey() {
  if (props.readonly) {
    return
  }

  emit('update:modelValue', !props.modelValue)
}
</script>

<style lang="scss" scoped>
.base-checkbox {
  @include typo-caption;

  --checkbox-size: 22px;
  --checkbox-icon-size: calc(var(--checkbox-size) + 4px);
  --checkbox-checked-color: v-bind(checkedcolorcss);

  @include bp-media('xs', 'sm') {
    --checkbox-size: 16px;
  }

  label {
    width: 100%;
    display: flex;
    align-items: center;
    cursor: pointer;
    gap: 8px;
  }

  &__wrapper {
    flex-shrink: 0;
    position: relative;
    align-self: flex-start;
    width: var(--checkbox-size, 22px);
    height: var(--checkbox-size, 22px);
  }

  &__checkbox {
    position: absolute;
    left: 0;
    top: 0;
    height: 100%;
    width: 100%;
    box-sizing: border-box;
    border: 2px solid var(--color-foreground-caption);
    border-radius: 4px;
    z-index: 1;
    background-color: transparent;
    transition: border-color 0.2s ease;
  }

  .base-checkbox__icon {
    opacity: 0;
    position: absolute;
    top: -2px;
    left: -2px;
    transition: all 0.2s ease;
    z-index: 2;
  }

  input {
    position: absolute;
    width: 0;
    height: 0;
    opacity: 0;
  }

  &__input--checked + &__icon {
    opacity: 1;
  }

  input:checked ~ &__checkbox {
    border-color: var(--checkbox-checked-color);
  }

  input:focus ~ &__checkbox {
    box-shadow: 0 0 13.8px -3px var(--color-foreground-caption);
  }

  input:checked:focus ~ &__checkbox {
    box-shadow: 0 0 13.8px -3px var(--checkbox-checked-color);
  }

  p {
    flex: 1;
    text-align: left;
    width: 100%;
    overflow: hidden;
  }
}
</style>
